import { useState } from "react";
import { Link, useLocation, useParams } from "wouter";
import { useAuth } from "../../auth/AuthContext";
import { isNotFound } from "../../api/errors";
import { useProject, useServices } from "../../api/queries";
import type { Service } from "../../api/types";
import { EmptyState, ErrorState, LoadingState } from "../../components/AsyncStates";
import { Button } from "../../components/Button";
import { DataTable, type DataTableColumn } from "../../components/DataTable";
import { PaginatorControls, usePaginator } from "../../components/Paginator";
import { QueryStates } from "../../components/QueryStates";
import { ServiceStatusPill } from "../../components/StatePill";
import { useFleet } from "../../fleet/FleetContext";
import { CreateServiceModal } from "../services/CreateServiceModal";
import "./projects.css";

const COLUMNS: DataTableColumn<Service>[] = [
  { key: "name", header: "Name", cell: (service) => service.name, className: "data-table__primary" },
  { key: "status", header: "Status", cell: (service) => <ServiceStatusPill status={service.status} /> },
  {
    key: "source",
    header: "Source",
    // A service is image-only or compose-only, never both (server/internal/
    // projectsvc/http.go's ServiceJSON) — render whichever is set.
    cell: (service) => service.image || (service.composeRef ? `stack: ${service.composeRef}` : "—"),
    className: "data-table__mono",
  },
  { key: "desiredCount", header: "Desired count", cell: (service) => service.desiredCount, className: "data-table__mono" },
  {
    key: "updatedAt",
    header: "Updated",
    cell: (service) => new Date(service.updatedAt).toLocaleString(),
    className: "data-table__secondary",
  },
];

export function ProjectDetailPage() {
  const { projectId } = useParams<{ projectId: string }>();
  const { currentId } = useFleet();
  const { session } = useAuth();
  const [, navigate] = useLocation();
  const [createServiceOpen, setCreateServiceOpen] = useState(false);

  const { data: project, isPending, isError, error, refetch } = useProject(currentId, projectId);
  const servicesPaginator = usePaginator(projectId ?? "");
  const {
    data: servicesPage,
    isPending: servicesPending,
    isError: servicesIsError,
    error: servicesError,
    refetch: refetchServices,
    isFetching: servicesFetching,
  } = useServices(currentId, projectId ?? "", { cursor: servicesPaginator.cursor });

  // Mirrors the server's authz matrix (service:create is granted to
  // operator/admin only) — this only hides the control (S-8).
  const canManage = session?.role === "admin" || session?.role === "operator";

  if (isPending) {
    return <LoadingState label="Loading project…" />;
  }

  if (isError) {
    if (isNotFound(error)) {
      return (
        <ErrorState
          title="Project not found"
          description="It may have been removed, or the link is out of date. Head back to the projects list."
        />
      );
    }
    return (
      <ErrorState
        description={error instanceof Error ? error.message : "Couldn't load this project."}
        onRetry={refetch}
      />
    );
  }

  if (!project) return null;

  const newServiceButton = (
    <Button variant="primary" onClick={() => setCreateServiceOpen(true)}>
      New service
    </Button>
  );

  return (
    <div className="project-detail">
      <Link to="/projects" className="project-detail__back">
        ← Back to projects
      </Link>

      <div className="project-detail__header">
        <div>
          <h1 className="project-detail__title">{project.name}</h1>
          {project.description && <p className="project-detail__description">{project.description}</p>}
        </div>
      </div>

      <dl className="project-detail__meta">
        <div>
          <dt>Created by</dt>
          <dd>{project.createdBy || "—"}</dd>
        </div>
        <div>
          <dt>Created</dt>
          <dd>{new Date(project.createdAt).toLocaleString()}</dd>
        </div>
        <div>
          <dt>Updated</dt>
          <dd>{new Date(project.updatedAt).toLocaleString()}</dd>
        </div>
      </dl>

      <div className="project-detail__services-header">
        <h2>Services</h2>
        {canManage && newServiceButton}
      </div>

      <QueryStates
        isLoading={servicesPending}
        loadingLabel="Loading services…"
        isError={servicesIsError}
        error={servicesError}
        onRetry={refetchServices}
        errorDescription={
          servicesError instanceof Error
            ? servicesError.message
            : "Couldn't load services. Check your connection and try again."
        }
        isEmpty={Boolean(servicesPage && servicesPage.items.length === 0)}
        empty={
          <EmptyState
            title="No services in this project yet"
            description="Create one to define an image (or a stack reference) and a desired instance count."
            action={canManage ? newServiceButton : undefined}
          />
        }
      >
        {servicesPage && (
          <>
            <DataTable
              columns={COLUMNS}
              rows={servicesPage.items}
              rowKey={(service) => service.id}
              onRowClick={(service) => navigate(`/projects/${project.id}/services/${service.id}`)}
              ariaBusy={servicesFetching}
            />
            <PaginatorControls
              itemCount={servicesPage.items.length}
              nextCursor={servicesPage.nextCursor}
              paginator={servicesPaginator}
              noun="services"
            />
          </>
        )}
      </QueryStates>

      {canManage && (
        <CreateServiceModal
          open={createServiceOpen}
          fleetId={currentId}
          projectId={project.id}
          onClose={() => setCreateServiceOpen(false)}
        />
      )}
    </div>
  );
}
